import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Box,
  Drawer,
  Container,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Dashboard,
  Logout,
} from '@mui/icons-material';
import ThemeToggle from '../../ui/ThemeToggle/ThemeToggle';
import { Link, useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { auth } from '../../../firebase';
import { useAuth } from '../../../context/AuthContext';
import GradientLogo from '../../ui/Logo/GradientLogo';
import axios from '../../../api/myAxios';
import ChangeLanguage from '../../ui/LanguageSelector/ChangeLanguage';
import { useTranslation } from 'react-i18next';
import { NavbarProps } from '../../../types';
import { useQuery, useQueryClient } from '@tanstack/react-query';

function Navbar({ isMobile, drawerOpen, toggleDrawer }: NavbarProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [, , removeCookie] = useCookies(['authToken']);
  const queryClient = useQueryClient();

  const { data: profile } = useQuery({
    queryKey: ['profile', currentUser?.uid],
    queryFn: async () => {
      const { data } = await axios.get('/api/users/profile');
      return data.user;
    },
    enabled: !!currentUser,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    removeCookie('authToken', { path: '/' });
    queryClient.clear();
    auth.signOut().then(() => {
      navigate('/login');
    });
  };

  const drawer = (
    <Box onClick={toggleDrawer} sx={{ width: 280 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2,
        }}
      >
        <GradientLogo isMobile={isMobile} />
      </Box>
      <Divider />
      {!currentUser && (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button variant="contained" fullWidth component={Link} to="/login">
            {t('navBar.login')}
          </Button>
          <Button variant="outlined" fullWidth component={Link} to="/signup">
            {t('navBar.register')}
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={toggleDrawer}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <GradientLogo isMobile={isMobile} />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip
                title={
                  theme.palette.mode === 'dark' ? 'Light mode' : 'Dark mode'
                }
              >
                <ThemeToggle variant="menu" />
              </Tooltip>
              <Box sx={{ ml: 1 }}>
                <ChangeLanguage isMobile={isMobile} variant="menu" />
              </Box>
              {currentUser ? (
                <>
                  {!isMobile && (
                    <Button
                      component={Link}
                      to="/dashboard"
                      color="inherit"
                      sx={{ ml: 1 }}
                    >
                      {t('navBar.dashboard')}
                    </Button>
                  )}
                  <Tooltip title="Profil">
                    <IconButton
                      onClick={handleProfileMenuOpen}
                      size="small"
                      sx={{ ml: 1 }}
                      aria-controls="menu-appbar"
                      aria-haspopup="true"
                    >
                      <Avatar
                        src={profile?.photoURL}
                        alt={profile?.displayName || 'User'}
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: theme.palette.primary.main,
                        }}
                      >
                        {profile?.photoURL ??
                          currentUser?.displayName?.charAt(0)}
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                !isMobile && (
                  <Box sx={{ display: 'flex', ml: 2 }}>
                    <Button
                      variant="outlined"
                      component={Link}
                      to="/login"
                      sx={{ mr: 1 }}
                    >
                      {t('navBar.login')}
                    </Button>
                    <Button variant="contained" component={Link} to="/signup">
                      {t('navBar.register')}
                    </Button>
                  </Box>
                )
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      {currentUser && (
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem component={Link} to="/profile" onClick={handleMenuClose}>
            <AccountCircle fontSize="small" sx={{ mr: 2 }} />{' '}
            {t('navBar.profile')}
          </MenuItem>
          {isMobile && (
            <MenuItem
              component={Link}
              to="/dashboard"
              onClick={handleMenuClose}
            >
              <Dashboard fontSize="small" sx={{ mr: 2 }} />{' '}
              {t('navBar.dashboard')}
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={handleLogout}>
            <Logout fontSize="small" sx={{ mr: 2 }} /> {t('navBar.logout')}
          </MenuItem>
        </Menu>
      )}

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={toggleDrawer}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

export default Navbar;
